import { Trans } from '@lingui/macro'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { PortalLanguage } from 'app/backend/salesforce-maps/CommonTypes'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { currencyFormatedString } from 'app/views/common/Formats'
import { FormElementTitle } from 'app/views/forms/common/FormElementTitle'
import { FormHelptext } from 'app/views/forms/common/FormHelpText'
import { Field, useField } from 'formik'
import { ReactElement, useMemo, useState } from 'react'
import { myI18n } from 'translation/I18nConnectedProvider'
import { isFalsy, isTruthy } from 'utilsTS'
import { budgetCentreGrantKey } from '../budget/FormBudget'
import { BudgetLine, BudgetSubKey, BudgetUpdateKey, BudgetUpdateLines, FormBudgetUpdateValue } from './budgetUpdateTypes'
import './FormBudgetUpdate.css'
import { budgetUpdateKeys, isBudgetDifferenceZero, isBudgetUpdateChangeEqual } from './formBudgetUpdateUtils'

type BudgetSumKey = 'revenues' | 'expenses' | 'difference'
type BudgetSums = Record<BudgetUpdateKey, number>

interface calculatedSums {
  revenues: BudgetSums
  expenses: BudgetSums
  difference: BudgetSums
}

const calculateSum = (
  lines: BudgetUpdateLines | undefined,
  budgetKey: BudgetUpdateKey,
  subKey: BudgetSubKey
): number => {
  let sum = 0
  if (lines != null) {
    Object.entries(lines).forEach(([key, obj]) => {
      const dataObj = obj[budgetKey]
      const value = dataObj?.[subKey]
      if (value != null) {
        sum += +value
      }
    })
  }

  return sum
}

const calculateBudgetSums = (
  revenuesLines: BudgetUpdateLines,
  expensesLines: BudgetUpdateLines
): calculatedSums => {
  const calculate = (lines: BudgetUpdateLines): BudgetSums =>
    Object.fromEntries(
      budgetUpdateKeys.map(({ key, subKey }) => [key, calculateSum(lines, key, subKey)])
    ) as BudgetSums

  const revenues = calculate(revenuesLines)
  const expenses = calculate(expensesLines)
  const difference = Object.fromEntries(
    budgetUpdateKeys.map(({ key }) => [key, revenues[key] - expenses[key]])
  ) as BudgetSums

  return { revenues, expenses, difference }
}

interface FormBudgetUpdateProps {
  id: string
  langVersion: PortalLanguage
  title: string
  typeProps: Object
  disabled: boolean
  helpText: string
  printable: boolean
}

export const FormBudgetUpdate: React.FC<FormBudgetUpdateProps> = ({
  id,
  langVersion,
  title,
  disabled,
  helpText,
  printable,
  ...props
}) => {
  const [field, , helpers] = useField<FormBudgetUpdateValue>(id)
  const { value } = field
  const { setValue } = helpers
  const calculatedSums = useMemo(
    () => calculateBudgetSums(value.revenuesLines, value.expensesLines),
    [value.revenuesLines, value.expensesLines]
  )
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false)

  const isBudgetUpdateActualError = !isBudgetDifferenceZero(calculatedSums.difference.budgetUpdateActual)
  const isApplicationBudgetError = !isBudgetDifferenceZero(calculatedSums.difference.applicationBudget)
  const budgetChanged = value?.report?.hasBudgetChanged === 'Yes'
  const revenuesMatchExpenses = isBudgetUpdateChangeEqual(value.revenuesLines, value.expensesLines)

  const editable = value.report != null &&
    ['Requested', 'More info required'].includes(value.report?.status ?? '') &&
    !disabled

  const checkIfBudgetWasChanged = (): boolean =>
    (Object.values({ ...value.expensesLines, ...value.revenuesLines })).some(
      ({ applicationBudget, budgetUpdateChange, latestUpdate }, index) => {
        if (isTruthy(latestUpdate?.change)) {
          return latestUpdate?.change !== budgetUpdateChange?.change
        }
        return applicationBudget?.value !== budgetUpdateChange?.change
      }
    )

  const isLatestUpdate = Boolean(value.latestReport)

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='xs'
        open={resetConfirmationOpen}
      >
        <DialogTitleWithIconClose
          label={<Trans>BUDGET_UPDATE_CONFIRM_RESET_DIALOG_TITLE</Trans>}
          handleClose={() => {
            setResetConfirmationOpen(false)
          }}
        />
        <DialogContent sx={{ padding: '0 48px 32px 48px' }}>
          <DialogContentText style={{ lineHeight: '20px' }}>
            <Trans>FORM_BUDGET_UPDATE_RESET_BUDGET_QUESTION</Trans>
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'stretch',
            padding: '0 48px 32px 48px',
            gap: 2,
            '& .MuiButton-root': {
              margin: 0
            }
          }}
        >
          <Grid container gap='16px'>
            <Button
              variant='contained'
              color='primary'
              fullWidth
              onClick={(e) => {
                const toSet = { ...value }
                toSet.report.hasBudgetChanged = 'No'
                const keys = ['expensesLines', 'revenuesLines']
                keys.forEach((key) => {
                  Object.entries(toSet[key]).forEach(([lineKey, value]) => {
                    toSet[key][lineKey].budgetUpdateChange = {
                      ...toSet[key][lineKey].budgetUpdateChange,
                      change: toSet[key][lineKey].defaultValue.value ?? 0,
                      changeDetails: toSet[key][lineKey].defaultValue.comment
                    }
                  })
                })

                void setValue(toSet)
                setResetConfirmationOpen(false)
              }}
            >
              <Trans>Yes</Trans>
            </Button>
            <Button
              variant='outlined'
              color='primary'
              fullWidth
              onClick={(e) => {
                setResetConfirmationOpen(false)
              }}
            >
              <Trans>No</Trans>
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
      <div style={{ textAlign: 'center' }}>
        {title != null &&
          <Typography className='form-budget-update-title'>
            {title}
          </Typography>}
        {helpText != null && !printable && <FormHelptext helpText={helpText} />}
      </div>
      <TableContainer
        className='form-budget-update-table-container'
        sx={(theme) => ({
          border: `1px solid ${theme.palette.primary.main}`
        })}
      >
        <Table className='form-budget-update-table'>
          <ColumnDefinitions isLatestUpdate={isLatestUpdate} />
          <TableHead>
            <TableRow>
              <TableCell className='header-cell'>
                <Typography className='cell-title'>
                  <Trans>BUDGET_UPDATE_ORIGINAL_BUDGET</Trans>
                </Typography>
              </TableCell>
              {isLatestUpdate && (
                <TableCell className='header-cell'>
                  <Typography className='cell-title'>
                    <Trans>BUDGET_UPDATE_LATEST_UPDATE</Trans>
                  </Typography>
                </TableCell>
              )}
              <TableCell className='header-cell'>
                <Typography className='cell-title pb-8'>
                  <Trans>BUDGET_UPDATE_HAS_YOUR_BUDGET_CHANGE</Trans>
                </Typography>
                <div className='mb-16'>
                  <Field name={id + '.report.hasBudgetChanged'}>
                    {({ field, form }) => (
                      <RadioGroup
                        {...field}
                        row
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value

                          if (newValue === 'No') {
                            if (checkIfBudgetWasChanged()) {
                              setResetConfirmationOpen(true)
                            } else {
                              const idKey = field.name.split('.')[0]
                              const toSet = structuredClone(form.values)
                              if (toSet[idKey].report != null) {
                                toSet[idKey].report.hasBudgetChanged = 'No'
                              }

                              const keys = ['expensesLines', 'revenuesLines']
                              keys.forEach((key) => {
                                if (isFalsy(toSet[key])) return
                                Object.entries(toSet[key]).forEach(([lineKey, value]) => {
                                  toSet[key][lineKey].budgetUpdateChange = {
                                    ...toSet[key][lineKey].budgetUpdateChange,
                                    changeDetails: ''
                                  }
                                })
                              })

                              form.setValues(toSet, true)
                            }
                          } else {
                            form?.setFieldValue(field.name, 'Yes', true)
                          }
                        }}
                      >
                        <FormControlLabel disabled={disabled} value='Yes' control={<Radio />} label={<Trans>Yes</Trans>} />
                        <FormControlLabel disabled={disabled} value='No' control={<Radio />} label={<Trans>No</Trans>} />
                      </RadioGroup>
                    )}
                  </Field>
                </div>
                {budgetChanged && !printable &&
                  <>
                    {revenuesMatchExpenses
                      ? (
                        <Alert severity='info' size='small' className='mb-16'>
                          <Typography className='alert-title'>
                            <Trans>BUDGET_UPDATE_HAS_TO_MATCH_ALERT</Trans>
                          </Typography>
                        </Alert>
                        )
                      : (
                        <Alert severity='error' size='small' className='mb-16'>
                          <Typography className='alert-title'>
                            <Trans>BUDGET_UPDATE_DOES_NOT_MATCH_ERROR</Trans>
                          </Typography>
                        </Alert>
                        )}
                    <Typography className='text'>
                      <Trans>BUDGET_UPDATE_PLEASE_INDICATE_CHANGE</Trans>
                    </Typography>
                  </>}
              </TableCell>
              <TableCell className='header-cell'>
                <Typography className='cell-title mb-36'>
                  <Trans>BUDGET_UPDATE_ACTUAL_EXPENSES_REVENUES</Trans>
                </Typography>
                <Typography className='text'>
                  <Trans>BUDGET_UPDATE_ACTUAL_EXPENSES_EXPLANATION</Trans>
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className='subject-cell'>
                <Typography className='cell-title'>
                  <Trans>BUDGET_UPDATE_REVENUES</Trans>
                </Typography>
              </TableCell>
              {[...Array(isLatestUpdate ? 3 : 2).keys()].map((index) => {
                return (
                  <TableCell
                    key={index}
                    className='subject-cell'
                  />
                )
              })}
            </TableRow>
            {value.revenuesLines != null &&
              Object.keys(value.revenuesLines)
                .sort((a, b) => value.revenuesLines[a]?.order - value.revenuesLines[b].order)
                .map((key, index) => {
                  const dataObj = value.revenuesLines[key]

                  return (
                    <BudgetUpdateRow
                      budgetLine={dataObj}
                      langVersion={langVersion}
                      lineKey={key}
                      key={key}
                      index={index}
                      budgetUnchanged={!budgetChanged}
                      id={id}
                      isExpense={false}
                      isLatestUpdate={isLatestUpdate}
                      editable={editable}
                      linesLength={Object.keys(value.revenuesLines).length}
                      printable={printable}
                    />
                  )
                })}
            <SumRow
              sx={(theme) => ({ backgroundColor: (theme as Theme).palette.flarePrimary.main })}
              label={<Trans>BUDGET_UPDATE_SUM_OF_REVENUE</Trans>}
              isLatestUpdate={isLatestUpdate}
              calculatedSums={calculatedSums}
              sumKey='revenues'
              langVersion={langVersion}
            />
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer
        className='form-budget-update-table-container mt-16'
        sx={(theme) => ({
          border: `1px solid ${theme.palette.primary.main}`
        })}
      >
        <Table className='form-budget-update-table'>
          <ColumnDefinitions isLatestUpdate={isLatestUpdate} />
          <TableBody>
            <TableRow>
              <TableCell className='subject-cell'>
                <Typography className='cell-title'>
                  <Trans>BUDGET_UPDATE_EXPENSES</Trans>
                </Typography>
              </TableCell>
              {[...Array(isLatestUpdate ? 3 : 2).keys()].map((index) => {
                return (
                  <TableCell
                    key={index}
                    className='subject-cell'
                  />
                )
              })}
            </TableRow>
            {value.expensesLines != null &&
                Object.keys(value.expensesLines)
                  .sort((a, b) => value.expensesLines[a].order - value.expensesLines[b].order)
                  .map((key, index) => {
                    const dataObj = value.expensesLines[key]
                    return (
                      <BudgetUpdateRow
                        budgetLine={dataObj}
                        lineKey={key}
                        langVersion={langVersion}
                        key={key}
                        index={index}
                        budgetUnchanged={!budgetChanged}
                        id={id}
                        isExpense
                        isLatestUpdate={isLatestUpdate}
                        editable={editable}
                        linesLength={Object.keys(value.expensesLines).length}
                        printable={printable}
                      />
                    )
                  })}

            <SumRow
              sx={(theme) => ({ backgroundColor: (theme as Theme).palette.flarePrimary.main })}
              label={<Trans>BUDGET_UPDATE_SUM_OF_EXPENSES</Trans>}
              isLatestUpdate={isLatestUpdate}
              calculatedSums={calculatedSums}
              sumKey='expenses'
              langVersion={langVersion}
            />
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer className='form-budget-update-table-container mt-16'>
        <Table className='form-budget-update-table'>
          <ColumnDefinitions isLatestUpdate={isLatestUpdate} />
          <TableBody>
            <SumRow
              cellClassName='summary-cell-total'
              textClassName='summary-text-total'
              sx={(theme) => ({ backgroundColor: (theme as Theme).palette.primary.main })}
              label={<Trans>BUDGET_UPDATE_TOTAL_REVENUES_AND_EXPENSES</Trans>}
              isLatestUpdate={isLatestUpdate}
              calculatedSums={calculatedSums}
              sumKey='difference'
              langVersion={langVersion}
              budgetUpdateActualError={isBudgetUpdateActualError && !printable}
              applicationBudgetError={isApplicationBudgetError && !printable}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

interface ColumnDefinitionsProps {
  isLatestUpdate: boolean
}

const ColumnDefinitions: React.FC<ColumnDefinitionsProps> = ({ isLatestUpdate }) => {
  const firstColWidth = isLatestUpdate ? '35%' : '45%'
  const remainingCols = isLatestUpdate ? 3 : 2
  const equalWidth = `${(100 - parseInt(firstColWidth)) / remainingCols}%`

  return (
    <colgroup>
      <col style={{ width: firstColWidth }} />
      {isLatestUpdate && <col style={{ width: equalWidth }} />}
      <col style={{ width: equalWidth }} />
      <col style={{ width: equalWidth }} />
    </colgroup>
  )
}

interface SumRowProps {
  label: ReactElement | string
  isLatestUpdate: boolean
  calculatedSums: calculatedSums
  sumKey: BudgetSumKey
  langVersion: string
  sx?: SxProps
  textClassName?: string
  cellClassName?: string
  applicationBudgetError?: boolean
  budgetUpdateActualError?: boolean
}

const SumRow: React.FC<SumRowProps> = ({
  label,
  isLatestUpdate,
  calculatedSums,
  sumKey,
  langVersion,
  sx,
  textClassName = '',
  cellClassName = '',
  applicationBudgetError = false,
  budgetUpdateActualError = false
}) => {
  return (
    <TableRow sx={sx}>
      <TableCell className={`summary-cell-primary ${cellClassName}`}>
        <div className='line-name-cell-inner'>
          <div className='line-name-column' />
          <Box
            className='summary-cell-primary-inner'
            sx={(theme) => ({
              backgroundColor: applicationBudgetError
                ? theme.palette.error.main
                : undefined
            })}
          >
            <Typography className={`summary-text ${textClassName}`}>
              {label}
            </Typography>
            <Typography className={`summary-text ${textClassName}`}>
              {currencyFormatedString(
                calculatedSums[sumKey]?.applicationBudget,
                langVersion)}
            </Typography>
          </Box>
        </div>

      </TableCell>

      {isLatestUpdate && (
        <TableCell className={`summary-cell ${cellClassName}`}>
          <div className='summary-cell-inner'>
            <Typography className={`summary-text ${textClassName}`}>
              {label}
            </Typography>
            <Typography className={`summary-text ${textClassName}`}>
              {currencyFormatedString(
                calculatedSums[sumKey]?.latestUpdate,
                langVersion)}
            </Typography>
          </div>
        </TableCell>
      )}

      <TableCell className={`summary-cell ${cellClassName}`}>
        <div className='summary-cell-inner'>
          <Typography className={`summary-text ${textClassName}`}>
            {label}
          </Typography>
          <Typography className={`summary-text ${textClassName}`}>
            {currencyFormatedString(
              calculatedSums[sumKey]?.budgetUpdateChange,
              langVersion)}
          </Typography>
        </div>
      </TableCell>

      <TableCell
        className={`summary-cell ${cellClassName}`}
        sx={(theme) => ({
          backgroundColor: budgetUpdateActualError
            ? theme.palette.error.main
            : undefined
        })}
      >
        <div className='summary-cell-inner'>
          <Typography className={`summary-text ${textClassName}`}>
            {label}
          </Typography>
          <Typography className={`summary-text ${textClassName}`}>
            {currencyFormatedString(
              calculatedSums[sumKey]?.budgetUpdateActual,
              langVersion)}
          </Typography>
        </div>
      </TableCell>
    </TableRow>
  )
}

interface BudgetUpdateRowProps {
  budgetLine: BudgetLine
  lineKey: string
  langVersion: PortalLanguage
  budgetUnchanged: boolean
  id: string
  isLatestUpdate: boolean
  isExpense: boolean
  editable: boolean
  printable: boolean
  index: number
  linesLength: number
}

export const BudgetUpdateRow: React.FC<BudgetUpdateRowProps> = ({
  budgetLine,
  lineKey,
  langVersion,
  budgetUnchanged,
  id,
  isLatestUpdate,
  isExpense,
  editable,
  printable = false,
  index,
  linesLength
}) => {
  const {
    applicationBudget,
    budgetUpdateChange,
    budgetUpdateActual,
    latestUpdate
  } = budgetLine
  const formikKey = isExpense ? 'expensesLines' : 'revenuesLines'

  return (
    <TableRow>
      <TableCell
        className='line-name-cell'
        sx={{
          ...(index === 0 && { paddingTop: '0px !important' }),
          ...(index === linesLength - 1 && { borderBottom: 'none !important' })
        }}
      >
        <div className='line-name-cell-inner'>
          <div className={
            printable
              ? 'line-name-column form-print-subtitle'
              : 'line-name-column'
            }
          >
            <Typography className='line-name-text'>
              {myI18n._(lineKey)}
            </Typography>
          </div>
          <div className={
            `amount-comments-column field-group-stack${
              index === linesLength - 1 ? ' last-row' : ''
            }`
          }
          >

            <div className='field-group'>
              <Typography
                className='field-label'
                sx={(theme) => ({
                  color: theme.palette.primary.main
                })}
              >
                <Trans>Amount</Trans>
              </Typography>
              <Typography className='field-value'>
                {currencyFormatedString(applicationBudget?.value, langVersion)}
              </Typography>
            </div>
            <div className='field-group'>
              <Typography
                className='field-label'
                sx={(theme) => ({
                  color: theme.palette.primary.main
                })}
              >
                <Trans>Comment</Trans>
              </Typography>
              <Typography className='field-value'>
                {Array.isArray(applicationBudget?.comments)
                  ? applicationBudget.comments.join('\n')
                  : ''}
              </Typography>
            </div>
          </div>
        </div>
      </TableCell>
      {isLatestUpdate && (
        <TableCell
          className='default-cell'
          sx={{
            ...(index === 0 && { paddingTop: '0px !important' }),
            ...(index === linesLength - 1 && { borderBottom: 'none !important' })
          }}
        >
          <div className='field-group-stack'>
            <div className='field-group'>
              <Typography
                className='field-label'
                sx={(theme) => ({
                  color: theme.palette.primary.main
                })}
              >
                <Trans>Amount</Trans>
              </Typography>
              <Typography className='field-value'>
                {currencyFormatedString(latestUpdate?.change, langVersion)}
              </Typography>
            </div>
            <div className='field-group'>
              <Typography
                className='field-label'
                sx={(theme) => ({
                  color: theme.palette.primary.main
                })}
              >
                <Trans>Comment</Trans>
              </Typography>
              <Typography className='field-value'>
                {latestUpdate?.changeDetails}
              </Typography>
            </div>
          </div>
        </TableCell>
      )}
      <TableCell
        className={printable ? 'default-cell' : 'editable-cell'}
        sx={{
          ...(index === 0 && { paddingTop: '0px !important' }),
          ...(index === linesLength - 1 && { borderBottom: 'none !important' })
        }}
      >
        {printable
          ? (
            <div className='field-group-stack'>
              <div className='field-group'>
                <Typography
                  className='field-label'
                  sx={(theme) => ({
                    color: theme.palette.primary.main
                  })}
                >
                  <Trans>Amount</Trans>
                </Typography>
                <Typography className='field-value'>
                  {currencyFormatedString(budgetUpdateChange?.change, langVersion)}
                </Typography>
              </div>
              <div className='field-group'>
                <Typography
                  className='field-label'
                  sx={(theme) => ({
                    color: theme.palette.primary.main
                  })}
                >
                  <Trans>Comment</Trans>
                </Typography>
                <Typography className='field-value'>
                  {budgetUpdateChange?.changeDetails}
                </Typography>
              </div>
            </div>
            )
          : (
            <div className='text-field-stack '>
              <div>
                <FormElementTitle
                  title={<Trans>Amount</Trans>}
                  required
                  disabled={budgetUnchanged || !editable}
                />
                <TextFieldWithFormik
                  name={id + `.${formikKey}.${lineKey}.budgetUpdateChange.change`}
                  disabled={budgetUnchanged || !editable}
                  fullWidth
                  variant='outlined'
                  type='currency'
                  required
                  language={langVersion}
                  helperArray={[
                    <span key='enterZero'>
                      <Trans>BUDGET_UPDATE_ENTER_ZERO_IF_NO_EXPENSE_REVENUE</Trans>{'. '}
                    </span>]}
                />
              </div>
              <div>
                <FormElementTitle
                  title={<Trans>Comment</Trans>}
                  required={Boolean(
                    budgetUpdateChange?.change != null &&
                    parseFloat(String(budgetUpdateChange?.change)) > 0 &&
                    +budgetUpdateChange.change !== +applicationBudget.value
                  )}
                  disabled={budgetUnchanged || !editable}
                />
                <TextFieldWithFormik
                  name={id + `.${formikKey}.${lineKey}.budgetUpdateChange.changeDetails`}
                  disabled={budgetUnchanged || !editable}
                  limit={500}
                  multiline
                  fullWidth
                  variant='outlined'
                  required={Boolean(
                    isTruthy(budgetUpdateChange.change) &&
                    +budgetUpdateChange.change !== +applicationBudget.value
                  )}
                />
              </div>
            </div>
            )}
      </TableCell>
      <TableCell
        className={printable ? 'default-cell' : 'editable-cell'}
        sx={{
          ...(index === 0 && { paddingTop: '0px !important' }),
          ...(index === linesLength - 1 && { borderBottom: 'none !important' })
        }}
      >
        {printable || lineKey === budgetCentreGrantKey
          ? (
            <div className='field-group-stack'>
              <div className='field-group'>
                <Typography
                  className='field-label'
                  sx={(theme) => ({
                    color: theme.palette.primary.main
                  })}
                >
                  <Trans>Amount</Trans>
                </Typography>
                <Typography className='field-value'>
                  {currencyFormatedString(
                    budgetUpdateActual?.actual,
                    langVersion
                  )}
                </Typography>
              </div>
              <div className='field-group'>
                <Typography
                  className='field-label'
                  sx={(theme) => ({
                    color: theme.palette.primary.main
                  })}
                >
                  <Trans>Comment</Trans>
                </Typography>
                <Typography className='field-value'>
                  {budgetUpdateActual?.actualComments ?? ''}
                </Typography>
              </div>
            </div>
            )
          : (
            <div className='text-field-stack '>
              <div>
                <FormElementTitle
                  title={<Trans>Amount</Trans>}
                  required
                  disabled={!editable}
                />
                <TextFieldWithFormik
                  name={id + `.${formikKey}.${lineKey}.budgetUpdateActual.actual`}
                  disabled={!editable}
                  fullWidth
                  variant='outlined'
                  type='currency'
                  required
                  language={langVersion}
                />
              </div>
              <div>
                <FormElementTitle
                  title={<Trans>Comment</Trans>}
                  required={Boolean(
                    budgetUpdateActual != null && +budgetUpdateActual.actual
                  )}
                  disabled={!editable}
                />
                <TextFieldWithFormik
                  name={id + `.${formikKey}.${lineKey}.budgetUpdateActual.actualComments`}
                  disabled={!editable}
                  limit={500}
                  multiline
                  fullWidth
                  variant='outlined'
                  required={Boolean(
                    budgetUpdateActual != null && +budgetUpdateActual.actual
                  )}
                />
              </div>
            </div>
            )}
      </TableCell>
    </TableRow>
  )
}
